/**
 * Owner: Masa Bayu
 * File: host-catalog-store.ts
 * Date:Time: 2026-09-21 08:42
 * Description: P6-08/RB-03 host catalog store — QXP refs · production CDN base on qxread.com.
 */

import type { QxreadCatalogSerialized } from '@qx/qxread-core';
import { createDemoCatalog } from '../publication/create-demo-catalog';
import { createProductionCatalog } from '../publication/create-production-catalog';

/** RB-03 — resolvable same-origin CDN (replaces synthetic cdn.qxread.host). */
export const P6_08_CDN_BASE = 'https://qxread.com/cdn/qxp/v1/' as const;
export const P6_08_DEMO_UPDATED_AT = '2026-09-21T07:22:00.000Z';

/** Host/runtime catalog record — infrastructure · not lifecycle authority. */
export type HostCatalogRecord = {
  readonly publicationId: string;
  readonly qxpPublicationRef: string;
  readonly qxpVersionRef: string;
  readonly integrityHash: string;
  readonly listedVersion: string;
  readonly title: string;
  readonly coverAssetRef: string | null;
  readonly packageObjectKey: string;
  readonly stale: boolean;
  readonly invalid: boolean;
  readonly isContentPlane: false;
  readonly mutatesQxp: false;
  readonly mutatesQxw: false;
  readonly exposesQxw: false;
};

export type HostCdnAssetKind = 'qxp_package' | 'cover' | 'resource';

export type HostCdnAssetRecord = {
  readonly assetRef: string;
  readonly kind: HostCdnAssetKind;
  readonly objectKey: string;
  readonly url: string;
  readonly isContentPlane: false;
  readonly mutatesQxp: false;
  readonly mutatesQxw: false;
};

export type HostCatalogStore = {
  readonly records: readonly HostCatalogRecord[];
  readonly assets: readonly HostCdnAssetRecord[];
  /** Metadata SoT for resolve — bound with store (demo or production). */
  readonly catalogEntries: readonly QxreadCatalogSerialized[];
  readonly cdnBase: typeof P6_08_CDN_BASE;
  readonly isContentPlane: false;
  readonly inventsCatalogAuthority: false;
  readonly demoSoT: boolean;
};

function packageObjectKey(qxpPublicationRef: string): string {
  return `packages/${qxpPublicationRef}/package.qxp`;
}

function packageUrl(qxpPublicationRef: string): string {
  return `${P6_08_CDN_BASE}${packageObjectKey(qxpPublicationRef)}`;
}

function coverObjectKey(coverAssetRef: string): string {
  return `assets/${coverAssetRef}.jpg`;
}

function coverUrl(coverAssetRef: string): string {
  return `${P6_08_CDN_BASE}${coverObjectKey(coverAssetRef)}`;
}

function fromCatalogEntry(entry: QxreadCatalogSerialized): HostCatalogRecord | null {
  const ref = entry.qxpPublicationRef;
  if (typeof ref !== 'string' || ref.length === 0) return null;
  if (!ref.startsWith('qxp_')) return null;
  const versionRef = entry.version?.qxpVersionRef;
  if (typeof versionRef !== 'string' || versionRef.length === 0) return null;
  const integrityHash = entry.version?.integrityHash;
  if (typeof integrityHash !== 'string' || integrityHash.length === 0) return null;
  const coverRaw = entry.metadata.coverAssetRef;
  const coverAssetRef = typeof coverRaw === 'string' ? coverRaw : null;
  return Object.freeze({
    publicationId: entry.publicationId,
    qxpPublicationRef: ref,
    qxpVersionRef: versionRef,
    integrityHash,
    listedVersion: entry.listedVersion ?? entry.version?.versionLabel ?? '1.0.0',
    title: entry.title,
    coverAssetRef,
    packageObjectKey: packageObjectKey(ref),
    stale: false as const,
    invalid: false as const,
    isContentPlane: false as const,
    mutatesQxp: false as const,
    mutatesQxw: false as const,
    exposesQxw: false as const,
  });
}

function buildStore(
  catalog: readonly QxreadCatalogSerialized[],
  demoSoT: boolean,
): HostCatalogStore {
  const records: HostCatalogRecord[] = [];
  const assets: HostCdnAssetRecord[] = [];
  const catalogEntries: QxreadCatalogSerialized[] = [];
  for (const entry of catalog) {
    const record = fromCatalogEntry(entry);
    if (record === null) continue;
    catalogEntries.push(entry);
    records.push(record);
    assets.push(
      Object.freeze({
        assetRef: record.qxpPublicationRef,
        kind: 'qxp_package' as const,
        objectKey: record.packageObjectKey,
        url: packageUrl(record.qxpPublicationRef),
        isContentPlane: false as const,
        mutatesQxp: false as const,
        mutatesQxw: false as const,
      }),
    );
    if (record.coverAssetRef !== null) {
      assets.push(
        Object.freeze({
          assetRef: record.coverAssetRef,
          kind: 'cover' as const,
          objectKey: coverObjectKey(record.coverAssetRef),
          url: coverUrl(record.coverAssetRef),
          isContentPlane: false as const,
          mutatesQxp: false as const,
          mutatesQxw: false as const,
        }),
      );
    }
  }

  records.push(
    Object.freeze({
      publicationId: 'qxrdpub_p6_08_missing_qxp',
      qxpPublicationRef: '',
      qxpVersionRef: 'qxp_ver_p6_08_bad',
      integrityHash: 'hash_bad',
      listedVersion: '0.0.0',
      title: 'Invalid missing QXP',
      coverAssetRef: null,
      packageObjectKey: '',
      stale: false as const,
      invalid: true as const,
      isContentPlane: false as const,
      mutatesQxp: false as const,
      mutatesQxw: false as const,
      exposesQxw: false as const,
    }),
    Object.freeze({
      publicationId: 'qxrdpub_p6_08_stale',
      qxpPublicationRef: 'qxp_pub_p6_08_stale',
      qxpVersionRef: 'qxp_ver_p6_08_stale',
      integrityHash: 'hash_stale_expected',
      listedVersion: '9.9.9',
      title: 'Stale host entry',
      coverAssetRef: null,
      packageObjectKey: packageObjectKey('qxp_pub_p6_08_stale'),
      stale: true as const,
      invalid: false as const,
      isContentPlane: false as const,
      mutatesQxp: false as const,
      mutatesQxw: false as const,
      exposesQxw: false as const,
    }),
  );

  return Object.freeze({
    records: Object.freeze(records),
    assets: Object.freeze(assets),
    catalogEntries: Object.freeze(catalogEntries),
    cdnBase: P6_08_CDN_BASE,
    isContentPlane: false as const,
    inventsCatalogAuthority: false as const,
    demoSoT,
  });
}

/** Seed host store from catalog QXP bindings + CDN asset map (demo SoT for harness). */
export function createHostCatalogStore(
  catalog: readonly QxreadCatalogSerialized[] = createDemoCatalog(),
): HostCatalogStore {
  return buildStore(catalog, true);
}

/** RB-03 production host catalog — demo SoT false. */
export function createProductionHostCatalogStore(): HostCatalogStore {
  return buildStore(createProductionCatalog(), false);
}

export function listLiveHostCatalogRecords(
  store: HostCatalogStore,
): readonly HostCatalogRecord[] {
  return Object.freeze(
    store.records.filter(
      (r) =>
        !r.stale &&
        !r.invalid &&
        r.qxpPublicationRef.length > 0 &&
        r.exposesQxw === false,
    ),
  );
}

export function findHostCatalogRecord(
  store: HostCatalogStore,
  publicationId: string,
): HostCatalogRecord | null {
  return store.records.find((r) => r.publicationId === publicationId) ?? null;
}

export function findHostCdnAsset(
  store: HostCatalogStore,
  assetRef: string,
): HostCdnAssetRecord | null {
  return store.assets.find((a) => a.assetRef === assetRef) ?? null;
}

export function findStoreCatalogEntry(
  store: HostCatalogStore,
  publicationId: string,
): QxreadCatalogSerialized | null {
  return (
    store.catalogEntries.find((e) => e.publicationId === publicationId) ?? null
  );
}

let activeHostStore: HostCatalogStore | null = null;

export function getActiveHostCatalogStore(): HostCatalogStore | null {
  return activeHostStore;
}

export function setActiveHostCatalogStore(store: HostCatalogStore | null): void {
  activeHostStore = store;
}
